import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { Footer } from "@/components/Footer";
import { mdComponents } from "@/components/mdComponents";
import { ARCANES, ARCANE_BY_SLUG } from "@/data/tarot";
import { C } from "@/pages/Blog";
import { SITE, usePageMeta } from "@/lib/seo";

export const arcaneMeta = (a) => ({
  title: `${a.name} (${a.numeral}) : signification en amour · Tarot de Marseille | La Petite Boîte Intuitive`,
  description: `${a.name} au Tarot de Marseille : ${a.essence} Sa signification en amour et dans tes tirages.`,
  path: `/arcanes/${a.slug}`,
});

const jsonLd = (a) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: `${a.name} : signification au Tarot de Marseille`,
  description: a.essence,
  image: a.image,
  url: `${SITE}/arcanes/${a.slug}`,
  inLanguage: "fr-FR",
  author: { "@type": "Person", name: "Lucie" },
  publisher: { "@type": "Organization", name: "La Petite Boîte Intuitive", url: `${SITE}/` },
});

const NavLink = ({ a, dir }) => (
  <Link
    to={`/arcanes/${a.slug}`}
    data-testid={`arcane-${dir}`}
    className={`flex items-center gap-2 font-body text-xs uppercase tracking-[0.14em] transition-colors hover:text-[#E6C687] ${dir === "next" ? "ml-auto text-right" : ""}`}
    style={{ color: C.mist }}
  >
    {dir === "prev" && <ArrowLeft size={14} strokeWidth={1.6} />}
    {a.name}
    {dir === "next" && <ArrowRight size={14} strokeWidth={1.6} />}
  </Link>
);

const Detail = ({ a }) => {
  usePageMeta(arcaneMeta(a));
  const prev = ARCANES[(a.id + 21) % 22];
  const next = ARCANES[(a.id + 1) % 22];

  return (
    <div className="relative min-h-screen flex flex-col" style={{ backgroundColor: C.bg }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd(a)) }} />
      <div className="oracle-bg" />
      <div className="relative z-10 flex-1 max-w-xl w-full mx-auto px-6 py-14 sm:py-20">
        <Link to="/arcanes" data-testid="arcane-back-link" className="inline-flex items-center gap-2 font-body text-xs tracking-[0.12em] uppercase mb-12 transition-colors hover:text-[#E6C687]" style={{ color: C.mist }}>
          <ArrowLeft size={14} strokeWidth={1.6} />
          Les 22 arcanes
        </Link>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <img src={a.image} alt={`${a.name}, Tarot de Marseille`} data-testid="arcane-image" className="mx-auto w-48 sm:w-56 rounded-xl" style={{ border: "1px solid rgba(230,198,135,0.2)", boxShadow: "0 10px 44px rgba(0,0,0,0.55)" }} />
          <p className="font-body uppercase tracking-[0.3em] text-xs mt-8" style={{ color: C.goldLine }}>Arcane {a.numeral}</p>
          <h1 data-testid="arcane-title" className="font-display uppercase tracking-[0.06em] text-4xl sm:text-5xl font-light mt-3" style={{ color: C.title }}>{a.name}</h1>
          <p className="font-display italic text-xl mt-5" style={{ color: C.mist }}>{a.essence}</p>
          <span className="block mx-auto my-9 h-px w-16" style={{ backgroundColor: "rgba(230,198,135,0.4)" }} />
        </motion.div>
        <motion.div data-testid="arcane-text" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-left">
          <ReactMarkdown components={mdComponents}>{a.text}</ReactMarkdown>
          <p className="font-display italic text-base mt-6" style={{ color: C.goldLine }}>— Lucie</p>
        </motion.div>
        <div className="mt-12 rounded-2xl border p-8 text-center" style={{ borderColor: "rgba(230,198,135,0.22)", backgroundColor: "rgba(255,255,255,0.025)" }}>
          <p className="font-display text-2xl mb-5" style={{ color: C.title }}>Et si cette lame sortait pour toi ?</p>
          <Link to="/tirage" data-testid="arcane-cta-tirage" className="inline-flex items-center gap-2 rounded-full px-7 py-3 font-body text-xs uppercase tracking-[0.16em] transition-transform hover:-translate-y-0.5" style={{ backgroundColor: C.goldMat, color: C.bg }}>
            <Sparkles size={14} strokeWidth={1.6} />
            Faire mon tirage gratuit
          </Link>
        </div>
        <nav className="mt-12 flex gap-4" aria-label="Arcanes voisins">
          <NavLink a={prev} dir="prev" />
          <NavLink a={next} dir="next" />
        </nav>
      </div>
      <Footer />
    </div>
  );
};

export default function Arcane() {
  const a = ARCANE_BY_SLUG[useParams().slug];
  if (!a) return <Navigate to="/arcanes" replace />;
  return <Detail a={a} />;
}
